import { isEmpty } from "lodash";
import {
  Button,
  Flex,
  Modal,
  H3,
  Box,
  P,
  Popover,
  Small,
  Notification,
} from "orcs-design-system";
import pluralize from "pluralize";
import styled from "styled-components";

import ErrorNotification from "src/components/ErrorNotification";
import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import TagAttributesFields, {
  AdjustedTextInput,
  ParentTagsSection,
  UniqueAttributesSection,
} from "./TagAttributesFields";
import { hasEditableTagLevelFields } from "./TagAttributeModal.logic";

const DetailsWrapper = styled(Box)`
  overflow-y: auto;
  max-height: 150px;
`;

const TagAttributesDetailsColumn = (p) => (
  <Box mr="r" width="50%">
    <Flex flexDirection="column" gap="r">
      <AdjustedTextInput
        name="tag-type"
        type="text"
        id="tagType"
        label="Tag type"
        fullWidth
        value={p.tagTypeName}
        disabled
      />
      {!p.isDisplayValueEditable && (
        <AdjustedTextInput
          data-testid="tag-display-input"
          name="tag-display-value"
          type="text"
          id="tag-value-textinput"
          label="Tag display value"
          fullWidth
          value={p.displayValue}
          disabled
        />
      )}
      {p.hasDetails && (
        <Box>
          <P mb="4px">Tag type description</P>
          <DetailsWrapper p="s" boxBorder="default" borderRadius={2}>
            <RichTextContentViewer content={p.parsedDetails} fontSize="14px" />
          </DetailsWrapper>
        </Box>
      )}
      <TagAttributesFields
        attrTypeValues={p.readonlyAttrTypes}
        updatedAttributes={p.allAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode={p.includeTagLevelAttributes}
      />
    </Flex>
  </Box>
);

const TagAttributesEditColumn = (p) => (
  <Box width="50%">
    {hasEditableTagLevelFields({
      includeTagLevelAttributes: p.includeTagLevelAttributes,
      isDisplayValueEditable: p.isDisplayValueEditable,
      editableAttrTypes: p.editableAttrTypes,
    }) && (
      <Notification
        closable={false}
        variant="warning"
        icon="exclamation-triangle"
        mb="r"
      >
        Values changed below will apply to ALL people / teams that have this
        tag.
      </Notification>
    )}
    {p.isDisplayValueEditable && (
      <AdjustedTextInput
        data-testid="tag-display-input"
        name="tag-display-value"
        type="text"
        id="tag-value-name"
        label="Tag display value"
        fullWidth
        value={p.displayValue}
        onChange={p.onDisplayValueChange}
        mandatory
        disabled={!p.includeTagLevelAttributes}
      />
    )}
    <TagAttributesFields
      attrTypeValues={p.editableAttrTypes}
      updatedAttributes={p.allAttributes}
      onAttrValueChange={p.onAttrValueChange}
      onSelectAttrValue={p.onSelectAttrValue}
      onDateChange={p.onDateChange}
      fieldToBeFocused={p.fieldToBeFocused}
      fieldsToShowInvalidity={p.fieldsToShowInvalidity}
      featureFlags={p.featureFlags}
      tagTypesConfig={p.tagTypesConfig}
      editMode={p.includeTagLevelAttributes}
    />
  </Box>
);

const TagAttributesModalBody = (p) => (
  <>
    <Flex width="100%" p="xs" pt="r" flexDirection="column">
      {p.requiredFieldsMessage && (
        <Notification
          closable={false}
          variant="error"
          icon="exclamation-triangle"
          mb="r"
        >
          {p.requiredFieldsMessage}
        </Notification>
      )}
      <Flex width="100%">
        <TagAttributesDetailsColumn {...p} />
        <TagAttributesEditColumn {...p} />
        {p.error && (
          <ErrorNotification message={p.error?.message} error={p.error} />
        )}
      </Flex>
    </Flex>
    {!isEmpty(p.uniqueAttrTypes) && (
      <UniqueAttributesSection
        extraNotice={
          <Notification closable={false} icon="info-circle" mb="r">
            Values changed below will apply to ONLY this person / team that has
            this tag.
          </Notification>
        }
        attrTypeValues={p.uniqueAttrTypes}
        updatedAttributes={p.allAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        fieldToBeFocused={p.fieldToBeFocused}
        fieldsToShowInvalidity={p.fieldsToShowInvalidity}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode={p.editMode || p.canEditEntityTagAttributes}
      />
    )}
    {p.parentTag && p.parentTag.length > 0 && (
      <ParentTagsSection
        attrTypeValues={p.parentTag}
        updatedAttributes={p.allAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        fieldToBeFocused={p.fieldToBeFocused}
        fieldsToShowInvalidity={p.fieldsToShowInvalidity}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode={p.includeTagLevelAttributes}
      />
    )}
  </>
);

export const TagAttributesModalLayout = (p) => (
  <Modal
    width={["90vw", "90vw", "90vw", "80vw", "70vw", "50vw"]}
    height={["80vh", "80vh", "80vh", "80vh", "60vh"]}
    maxWidth="90vw"
    maxHeight="90vh"
    visible
    onClose={p.onClose}
    headerContent={
      <Flex px="xs">
        <Flex width="50%" alignItems="center" justifyContent="space-between">
          <H3>Tag details</H3>
          {p.appliedCount > 0 && (
            <Flex alignItems="center">
              <Popover variant="tooltip" />
              <Small ml="s">{`Tag applied ${p.appliedCount} ${pluralize(
                "time",
                p.appliedCount
              )}`}</Small>
            </Flex>
          )}
        </Flex>
        <Box width="50%">
          <H3 ml="19px">Tag attributes</H3>
        </Box>
      </Flex>
    }
    footerContent={
      <Flex pl="4px">
        <Button
          onClick={p.onSave}
          isLoading={p.isSaving}
          disabled={p.isSaving || !p.canSave}
          variant={p.isSaving || !p.canSave ? "disabled" : "success"}
          mr="s"
        >
          Save
        </Button>
        <Button onClick={p.onClose} variant="ghost">
          Close
        </Button>
      </Flex>
    }
  >
    <TagAttributesModalBody {...p} />
  </Modal>
);
